/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Category from '@linc-technologies/linced-common/models/goals/category';
import type Goal from '@linc-technologies/linced-common/models/goals/goal';
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: goals.GoalSubcategory.
export default class Subcategory extends Model {
  declare [Type]: 'goals/subcategory';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('goals/subject', { async: true, inverse: null })
  declare subjectId: AsyncBelongsTo<Subject>;

  @alias('subjectId')
  declare subject: AsyncBelongsTo<Subject>;

  @alias('subject.id')
  declare subject_id: string | null | undefined;

  @attr('boolean', { defaultValue: false })
  declare isPLP: boolean;

  @belongsTo('goals/category', { async: true, inverse: 'subcategories' })
  declare categoryId: AsyncBelongsTo<Category>;

  @alias('categoryId')
  declare category: AsyncBelongsTo<Category>;

  @alias('category.id')
  declare category_id: string | null | undefined;

  @hasMany('goals/goal', { async: true, inverse: 'subcategoryId' })
  declare goals: AsyncHasMany<Goal>;

  @computed('goals.content.[]')
  get goalsArray() {
    return resolved(this.goals);
  }

  goalSortOrder = Object.freeze(['sequence', 'label']);

  @sort('goalsArray', 'goalSortOrder')
  declare sortedGoals: unknown[];

  @computed('sortedGoals.[]', 'categoryId')
  get sortedBoundGoals() {
    const goals: unknown[] & {
      __category__?: unknown;
      __subcategory__?: Subcategory;
    } = this.sortedGoals.slice();
    // The loaded category, not the async relationship's proxy: sortDragEnd
    // sets it as categoryId, and WarpDrive rejects a proxy there.
    goals.__category__ = (this as Subcategory).belongsTo('categoryId').value();
    goals.__subcategory__ = this;
    return goals;
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare key: string;
}
